import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import authFetch from '../../axios';

import {
  InteractiveMapType,
  RootState,
  ModelType,
} from '../../store';

import eventBus from '../../eventBus';
import { saveState } from '../autoSaver';

import { MarkerType, MarkerDecorationType } from '../types';

// For using in builder.addMatcher
// Returns true if action returns API data for InteractiveMap
// so we can update redux on all this actions
export function hasMapResponse(action:PayloadAction) {
  return (
    action.type === 'api/fetchAPIData/fulfilled'
      || action.type === 'api/saveAPI/fulfilled'
  );
}
export type MapResponseAction = PayloadAction<InteractiveMapType>;

// Rejection value when the map was saved elsewhere after we loaded it
export const STALE_MAP = 'stale_map';

// Saving an outdated map would delete the records created meanwhile, so the
// only safe way forward is reloading it
export function askReloadStaleMap() {
  eventBus.emit(
    'ask',
    'This map was saved elsewhere (another tab or user) after you opened it. '
    + 'Reload to get the latest version; your unsaved changes here can\'t be saved.',
    'Reload',
    'Cancel',
    () => window.location.reload(),
    () => {},
  );
}

type InfoApiType = {
  id: number;
  mapId: number;
  vrId: number;
  subtitle: string;
  permalink: string;
  splashImage: string;
  description: string;
  coverImage: string;
  loaderBgColor: string;
  loaderLogoBgColor: string;
};

// Fetch order info
// Used to display property logo in Loader
export const fetchOrderInfo = createAsyncThunk(
  'api/fetchOrderInfo',
  async (
    payload: { orderId: string, mapId?: string, vrId?: string }, { _rejectWithValue }: any
  ): Promise<InfoApiType> => {
    try {
      const { orderId, mapId, vrId } = payload;
      const response = await authFetch(`/api/orders/${orderId}/interactive_maps/info?id=${mapId}&vr_id=${vrId}`);
      return response.data;
    } catch (error: any) {
      // TODO Actually handle the errors, as this just assumes everything worked
      return error.response.data;
    }
  },
);

export const fetchAPIData = createAsyncThunk(
  'api/fetchAPIData',
  async (payload: { orderId:string, mapId:string|undefined }, { rejectWithValue }: any): Promise<InteractiveMapType> => {
    try {
      const { orderId, mapId } = payload;
      const baseUrl = `/api/orders/${orderId}/interactive_maps`;
      const response = await authFetch(`${baseUrl}/${mapId}`);
      const data = response.data as any;
      return {
        ...data,
        default_image: data.default_image ?? 0,
        customCss: data.customCss ?? '',
        markers: data.markers,
      };
    } catch (error: any) {
      if (error.message === 'Request failed with status code 402') {
        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  },
);

export const fetchAPIVrData = createAsyncThunk(
  'api/fetchAPIVrData',
  async (payload: { orderId: string, vrId: string }, { rejectWithValue }: any): Promise<ModelType> => {
    try {
      const { orderId, vrId } = payload;
      const response = await authFetch(`/api/orders/${orderId}/vrs/${vrId}`);
      return response.data;
    } catch (error: any) {
      if (error.message === 'Request failed with status code 402') {
        return rejectWithValue(error.response.data.error);
      } else {
        throw error;
      }
    }
  },
);

export const saveAPI = createAsyncThunk<InteractiveMapType, void, { rejectValue: string }>(
  'api/saveAPI',
  async (_arg, { getState, rejectWithValue }) => {
    const state = getState() as RootState;

    try {
      const {
        undoable: {
          present: {
            images: { list: images },
            spaces: { list: spaces, categories },
            labels: { list: labels },
            markers: { list: markers, decorations },
          },
        },
        map,
        paths,
      } = state;

      const persistedDecorations = decorations.filter((d) => d.id !== 'default');
      const mapPayload = {
        ...map,
        images,
        spaces,
        categories,
        labels,
        markers,
        markerDecorations: persistedDecorations,
      };

      const response = await authFetch.put(`/api/orders/${map.order.id}/interactive_maps/${map.id}`, {
        interactive_map: {
          map: mapPayload,
          images,
          spaces,
          categories,
          labels,
          markers,
          decorations: persistedDecorations,
          paths,
        }
      });

      const data = response.data;
      return {
        ...data,
        default_image: data.default_image ?? 0,
        customCss: data.customCss ?? '',
        current_user: data.current_user ?? map.current_user,
        markers: data.markers,
        markerDecorations: data.markerDecorations,
      } as InteractiveMapType;
    } catch (error: any) {
      if (error.response?.status === 409) {
        // Our draft is outdated too, don't offer recovering it after reloading
        saveState(undefined, `${state.map.order.id}-${state.map.id}`);
        return rejectWithValue(STALE_MAP);
      } else if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.error || 'Unprocessable entity');
      } else {
        throw error;
      }
    }
  },
);

export interface APIState {
  data: InteractiveMapType | null;
  loadStatus: 'idle' | 'loading' | 'success' | 'failed';
  saveStatus: 'idle' | 'unsaved' | 'saving' | 'success' | 'failed';
  error: string | null;
  savedAt: number;
}

const initialState: APIState = {
  data: null,
  loadStatus: 'idle',
  saveStatus: 'idle',
  error: null,
  savedAt: 0,
};

export const apiSlice = createSlice({
  name: 'api',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAPIData.pending, (state) => ({ ...state, loadStatus: 'loading' }))
      .addCase(fetchAPIData.fulfilled, (state, { payload }) => ({
        ...state,
        loadStatus: 'success',
        data: payload as InteractiveMapType,
        savedAt: 0,
      }))
      .addCase(fetchAPIData.rejected, (state, { payload, error }) => ({
        ...state,
        loadStatus: 'failed',
        error: (payload as string) || error.message || null,
      }));
    builder
      .addCase(fetchAPIVrData.pending, (state) => ({ ...state, loadStatus: 'loading' }))
      .addCase(fetchAPIVrData.fulfilled, (state, { payload }) => {
        state.data = {
          id: 0,
          order: { id: 0, permalink: '', description: '' },
          markerId: '',
          markers: [] as MarkerType[],
          defaultMarkers: {} as MarkerType,
          markerDecorations: [] as MarkerDecorationType[],
          default_image: 0,
          name: '',
          featured_video: '',
          featured_image: null,
          groupBy: 'categories',
          allowLabels: true,
          categories: [],
          spaces: [],
          labels: [],
          images: [],
          models: [],
          current_user: payload.current_user,
          customCss: '',
          primaryColor: '',
          secondaryColor: '',
          bgColor: '',
          coverImage: '',
        };
        state.loadStatus = 'success';
      })
      .addCase(fetchAPIVrData.rejected, (state, { payload, error }) => ({
        ...state,
        loadStatus: 'failed',
        error: (payload as string) || error.message || null,
      }));

    builder
      .addCase(saveAPI.pending, (state) => {
        state.saveStatus = 'saving';
      })
      .addCase(saveAPI.fulfilled, (state) => {
        state.saveStatus = 'success';
        state.savedAt = Date.now();
        eventBus.emit('flash', 'success', 'Changes saved.');
      })
      .addCase(saveAPI.rejected, (state, { payload, error }) => {
        state.saveStatus = 'failed';
        state.error = payload || error.message || null;
        if (payload === STALE_MAP) {
          askReloadStaleMap();
        } else {
          eventBus.emit('flash', 'error', `Could not save map: ${payload || error.message}`);
        }
      });
  },
});

export const mapNeedsSaveSelector = (state: RootState) => {
  return (
    // We did undo some already saved changes
    state.undoable.future.filter((s) => s.api.savedAt > state.undoable.present.api.savedAt).length > 0 ||
    // or we made some changes not yet saved
    state.undoable.past.filter((s) => s.api.savedAt >= state.undoable.present.api.savedAt).length > 0 ||
    state.map.needsSave ||
    state.paths.needsSave
  );
};

export const apiErrorSelector = (state: RootState) => state.undoable.present.api.error;

export const apiLoadingStatusSelector = (state: RootState) => state.undoable.present.api.loadStatus;
export const apiSavingStatusSelector = (state: RootState) => state.undoable.present.api.saveStatus;

export default apiSlice.reducer;
